import Model from 'src/app/common/model';
import TransactionsModel from './transactions.model';

/**
 * @model AttachmentsModel
 * @description
 * Represents the data structure for file attachments within the AquaCloud application.
 * This model serves as a Data Transfer Object (DTO) for handling file uploads, downloads,
 * and metadata management associated with various entities, particularly transactions.
 *
 * **Key Responsibilities:**
 * 1. **Metadata Storage**: Stores essential file attributes like name, type, and description.
 * 2. **Data Transport**: Carries the actual file content (often as a Base64 string in the `data` field) during upload operations.
 * 3. **Association**: Links attachments to specific transactions via `transaction_id` and the `Transaction` object.
 * 4. **Retrieval**: Provides the `attachment_path` for accessing stored files from the media server.
 *
 * **Data Flow:**
 * - **Upload**: A component populates `data` (Base64), `file_name`, and `file_type`. The service sends this to the backend.
 * - **Download/View**: The backend returns the model with `attachment_path`. The UI uses this path to display the image or download the file.
 *
 * **Usage:**
 * - Used by `AttachmentsService` for CRUD operations.
 * - Used in `MaterialTransactionComponent` or similar features where proof of transaction (receipts, invoices) is required.
 *
 * @extends Model
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
export default class AttachmentsModel extends Model {
	/**
	 * @type {TransactionsModel}
	 * @description The full transaction object associated with this attachment.
	 * @optional
	 */
	Transaction?: TransactionsModel;

	/**
	 * @type {number}
	 * @description The unique identifier of the transaction this attachment belongs to.
	 * Acts as a foreign key.
	 * @optional
	 */
	transaction_id?: number;

	/**
	 * @type {Text}
	 * @description The original name of the file (e.g., "invoice.pdf").
	 * @optional
	 */
	file_name?: Text;

	/**
	 * @type {Text}
	 * @description A brief description or note about the attachment.
	 * @optional
	 */
	description?: Text;

	/**
	 * @type {Text}
	 * @description The MIME type or extension of the file (e.g., "application/pdf", "image/png").
	 * @optional
	 */
	file_type?: Text;

	/**
	 * @type {Text}
	 * @description The relative or absolute URL path where the file is stored on the server.
	 * @optional
	 */
	attachment_path?: Text;

	/**
	 * @type {string}
	 * @description The raw data of the file, typically encoded as a Base64 string.
	 * Used primarily when uploading a new file from the client to the server.
	 */
	data: string;

	/**
	 * @constructor
	 * @description Initializes a new instance of the AttachmentsModel.
	 * Calls the parent `Model` constructor.
	 */
	constructor() {
		super();
	}
}
